import { DataObject, ReportRecord } from '../../../../models/report.model';
import { concatValuesBy, defaultTxt } from '@miq/utils';
import { mapAndFormatDynamicValue } from '../../utils/dynamic-data-retrievers';
import { digger } from '@cbre/digger';
import { sortAttributesByFieldId } from '../../utils/argument-sorting';
import { DisplayFnArgumentsInfo } from '../../../../models/template.model';

export const defaultDisplayFn = (record: ReportRecord, options, args, fieldId) => {
  const finalArgs = sortAttributesByFieldId(options.locale, args, fieldId);
  const values = finalArgs.map(arg => mapAndFormatDynamicValue(record, arg));
  return concatValuesBy(', ', ...values);
};

export const SINGULAR_UOM_LABELS = {
  years: 'Annually',
  months: 'Monthly',
  weeks: 'Weekly',
};

export const PLURAL_UOM_LABELS = {
  years: 'yearly',
  months: 'monthly',
  weeks: 'weekly',
};

export enum RENT_REVIEW_PROVISION_VALS {
  cap = 'cap',
  collar = 'collar',
  fixed = 'fixed',
}

export const RENT_REVIEW_PROVISION_EXCLUDE_LIST: string[] = [
  RENT_REVIEW_PROVISION_VALS.cap,
  RENT_REVIEW_PROVISION_VALS.collar,
];

export const CAP_COLLAR_FIXED_FIELD_LABEL_OPTIONS = {
  fixed: 'Fixed %',
  cap_collar_fixed: 'Cap and Collar / Fixed %',
  cap_collar_: 'Cap and Collar',
};

/**
 * Get from the rent reviews array the values of Cap, Collar and Fixed
 * @param record
 * @param rentReviewProvisionArgs
 * @returns string
 */
export const getCapCollarFixedValuesAsString = (
  record: ReportRecord,
  rentReviewProvisionArgs: DisplayFnArgumentsInfo[]
): string => {
  const label = [];
  // 17338 -- Options like Cap, Collar or fixed
  const rrpOptionConfig = rentReviewProvisionArgs.find(arg => arg && arg.id === 17338);
  // 17340 -- Percentage for options like 1, 3, 25 .. etc
  const rrpPercentageConfig = rentReviewProvisionArgs.find(arg => arg && arg.id === 17340);

  // Get the correspondings paths
  const path = rrpOptionConfig.field.split('/');
  const rrvPath = path[0];
  const rrvProvisionPath = path[1];
  const optionAttrName = path[2];

  // Get attribute name of the attribute related to the percentage selected
  let percentageAttrName: string[] | string = rrpPercentageConfig.field.split('/');
  percentageAttrName = percentageAttrName[percentageAttrName.length - 1];

  // Get all the rent reviews as a list of objects
  let listOfRentReviews = digger(record.data, rrvPath);

  if (!listOfRentReviews || !listOfRentReviews.length) {
    return defaultTxt;
  }

  listOfRentReviews
    .filter(rentReviewHasStartDate)
    .forEach(rrv =>
      label.push(
        getCapCollarFixedFormattedValue(rrv, rrvProvisionPath, optionAttrName, percentageAttrName as string) ||
          defaultTxt
      )
    );

  return label.length ? label.join(', ') : defaultTxt;
};

/**
 * Get the cap collar and fixed values with the correct format to display
 * @param rentreview
 * @param rrvProvisionPath
 * @param optionAttrName
 * @param percentageAttrName
 * @returns
 */
const getCapCollarFixedFormattedValue = (
  rentreview: DataObject,
  rrvProvisionPath: string,
  optionAttrName: string,
  percentageAttrName: string
): string => {
  let formattedValue = '';
  const rrpVals: DataObject[] = rentreview[rrvProvisionPath];
  // Get Cap, Collar and fixed values
  const capVal = rrpVals.find(val => val[optionAttrName].toLowerCase() === RENT_REVIEW_PROVISION_VALS.cap);
  const collarVal = rrpVals.find(val => val[optionAttrName].toLowerCase() === RENT_REVIEW_PROVISION_VALS.collar);
  const fixedVal = rrpVals.find(val => val[optionAttrName].toLowerCase() === RENT_REVIEW_PROVISION_VALS.fixed);

  /**
   * The value is displayed as:
   *  If only cap then: Cap and Collar 1--%
   *  If only collar then: Cap and Collar --4%
   *  If only fiexed then: Fixed 6%
   *  If cap and collar then: Cap and Collar 1-4%
   *  If cap, collar and fixed then: Cap and Collar / Fixed % 1-4%,3.6%
   */
  if (capVal || collarVal) {
    formattedValue = `${capVal ? capVal[percentageAttrName] : defaultTxt}-${
      collarVal ? collarVal[percentageAttrName] : defaultTxt
    }%`;
  }

  if (fixedVal) {
    const fixedLabel = `${fixedVal[percentageAttrName]}% ${fixedVal[optionAttrName].toLowerCase()}`;
    formattedValue = formattedValue.length ? `${formattedValue}, ${fixedLabel}` : fixedLabel;
  }

  return formattedValue;
};

/**
 * Get the label to show according the rent review values,
 * this function only takes in consideration the categories Cap, Collar and Fixed
 * @param record
 * @param rentReviewProvisionArgs
 * @returns
 */
export const getCapCollarFixedLabel = (record: ReportRecord, rentReviewProvisionArgs: DisplayFnArgumentsInfo[]) => {
  const acceptedVals: string[] = [
    RENT_REVIEW_PROVISION_VALS.cap,
    RENT_REVIEW_PROVISION_VALS.collar,
    RENT_REVIEW_PROVISION_VALS.fixed,
  ];
  const defaultLabel = CAP_COLLAR_FIXED_FIELD_LABEL_OPTIONS.cap_collar_;

  // 17338 -- Options like Cap, Collar or fixed
  const rrpOptionConfig = rentReviewProvisionArgs.find(arg => arg && arg.id === 17338);

  // Get the list of all the rent review provisions
  let path: string[] | string = rrpOptionConfig.path.split('.');
  const rrpOptionAttrName = path[2];
  path = `${path[0]}.${path[1]}`;

  let rentReviewProvisionsList: string[] = digger(record.data, path);

  if (!rentReviewProvisionsList || !rentReviewProvisionsList.length) {
    return defaultLabel;
  }
  //Get just the data we need from the object
  rentReviewProvisionsList = rentReviewProvisionsList.map(rrp => rrp[rrpOptionAttrName].toLowerCase());
  // Filter to exclude the values other than cap, collar or fixed
  rentReviewProvisionsList = rentReviewProvisionsList.filter(val => acceptedVals.includes(val));

  //Create a string to be used as an index to get the label
  const labelMapIndex = `${
    rentReviewProvisionsList.includes(RENT_REVIEW_PROVISION_VALS.cap) ||
    rentReviewProvisionsList.includes(RENT_REVIEW_PROVISION_VALS.collar)
      ? 'cap_collar_'
      : ''
  }${rentReviewProvisionsList.includes(RENT_REVIEW_PROVISION_VALS.fixed) ? 'fixed' : ''}`;

  return labelMapIndex && CAP_COLLAR_FIXED_FIELD_LABEL_OPTIONS[labelMapIndex]
    ? CAP_COLLAR_FIXED_FIELD_LABEL_OPTIONS[labelMapIndex]
    : defaultLabel;
};

/**
 * Get the rent reviews categories that are not Cap or Collar
 * @param record
 * @param rentReviewProvisionArgs
 * @returns
 */
export const getRentReviewProvisions = (record: ReportRecord, rentReviewProvisionArgs: DisplayFnArgumentsInfo[]) => {
  const formattedValue = [];

  // 17338 -- Options like Cap, Collar or fixed
  const rrpOptionConfig = rentReviewProvisionArgs.find(arg => arg && arg.id === 17338);
  const path = rrpOptionConfig.field.split('/');

  // Get the correspondings paths
  const rrvPath = path[0];
  const rrvProvisionPath = path[1];
  const optionAttrName = path[2];

  // Get all the rent reviews as a list of objects
  let listOfRentReviews = digger(record.data, rrvPath);

  if (!listOfRentReviews || !listOfRentReviews.length) {
    return defaultTxt;
  }

  listOfRentReviews.filter(rentReviewHasStartDate).forEach(rrv => {
    const rrpVals: DataObject[] = rrv[rrvProvisionPath];
    let filteredVals = rrpVals.map(rrp => rrp[optionAttrName]);
    filteredVals = filteredVals.filter(rrp => !RENT_REVIEW_PROVISION_EXCLUDE_LIST.includes(rrp.toLowerCase()));

    formattedValue.push(filteredVals.join('/') || defaultTxt);
  });

  return formattedValue.join(', ');
};

/**
 * Validate if the rent review option has a start date
 * if does not have start date then is not the kind of rent review that it is displayed in the ppt
 * @param rentReview
 * @returns
 */
export const rentReviewHasStartDate = rentReview => rentReview.arrangement_option_start_date != null;
